import { LAYOUT_STRIDE as S } from '../navLayers.js';
import { SCAN_CG } from '../../config.js';
import { guessFurniture } from './guessFurniture.js';

/**
 * スキャンから取り出した見取り（mapLayout.simplifyLayout の結果）を、
 * buildHouse.js が組み立てられる設計データ（rooms / walls / furniture）に変換する。
 *
 * これが「スキャンに CG を肉付けする」処理の中身。
 * 写真や間取り図からの推測ではなく、スキャンで測った床の高さ（中二階・ロフトも）、壁の位置、
 * 家具の位置と色をそのまま使う。
 *
 * - 床   … 同じ高さのマスをまとめて長方形にし、木目の床にする。階段の段は下まで詰めた段にする
 * - 壁   … 壁のマスを線状にまとめ、漆喰の壁にする
 * - 天井 … 当たり判定マップが持つ天井の高さ（階ごとの中央値）に板を張る
 * - 家具 … 隣り合う家具のマスをひとかたまりにして、スキャンの色を付けた箱で置く
 *
 * @param layout simplifyLayout の結果
 * @param nav    navLayers の当たり判定マップ（天井の高さに使う）。無くてもよい
 * @param spots  [{ x, y, z }] 視点の足元。内見の視点は必ず床の上なので、
 *               スキャンでそこの床を拾えていなくても CG では床を敷く
 */
export function planFromLayout(layout, { nav = null, spots = [] } = {}) {
  const { cell, minX, minZ } = layout;
  const toX = (gx) => gx * cell + minX;
  const toZ = (gz) => gz * cell + minZ;

  // ---- 床: 高さ（上面・下面）ごとに分けて長方形にまとめる ----
  const floorGroups = groupBy(layout.floors, (b, t) => `${b}:${t}`);
  fillFloorUnderFurniture(floorGroups, layout);
  extendFloorsToWalls(floorGroups, layout);
  const levelCounts = new Map();
  for (const { cells, top } of floorGroups.values()) {
    const key = round(top, 1);
    levelCounts.set(key, (levelCounts.get(key) ?? 0) + cells.length);
  }
  // 面積の大きい高さを「階」とみなす（それ以外の高さは階段の段）
  const minCells = SCAN_CG.levelMinAreaM2 / (cell * cell);
  const levels = clusterLevels(levelCounts, minCells);
  const levelIndexOf = (top) => levels.findIndex((h) => Math.abs(h - top) <= SCAN_CG.levelTolerance);

  // スキャンが「縦の面」と見た所。床を埋めるときの仕切りに使う（洗面所の中などが外扱いにならないように）
  const scanWalls = new Set();
  for (let i = 0; i < layout.walls.length; i += S) scanWalls.add(`${layout.walls[i]},${layout.walls[i + 1]}`);

  // 階ごとに「部屋の形」を決める。縁のギザギザをならし、中の抜けを埋めた床のマス
  const spotCells = spotCellsByLevel(spots, levels, layout);
  const levelFill = fillLevels(floorGroups, levels, levelIndexOf, spotCells, scanWalls);

  /*
   * ---- 階の高さから外れた「段」の仕分け ----
   * スキャンは机やベッドの上面も「立てる面」と見るので、家具の天板が段として残る。
   * さらにスキャンの床自体が波打つため、5cm〜40cmの細かい段が部屋じゅうに残る。
   * そのまま床にすると、床が段々畑のようになって部屋に見えない。
   *
   * 段を3つに分ける。
   *   家具  … 大きさが家具に合うもの（机・ベッドなど）
   *   階段  … 隣に一段違いの段があるもの
   *   平ら  … それ以外。スキャンの波打ちなので、一番近い階の床に吸収する
   */
  const stepTops = new Map();
  for (const g of floorGroups.values()) {
    if (levelIndexOf(g.top) >= 0) continue;
    for (const c of g.cells) stepTops.set(`${c.gx},${c.gz}`, g.top);
  }

  const furniture = [];
  const asFurniture = new Map();
  const asFloor = new Map();
  for (const [key, g] of floorGroups) {
    if (levelIndexOf(g.top) >= 0) continue;
    const floorY = nearestLevel(levels, g.bottom);
    const used = new Set();
    const flat = new Set();
    for (const part of connectedGroups(new Set(g.cells.map((c) => `${c.gx},${c.gz}`)))) {
      const guess = guessFurniture(part, { cell, minX, minZ, floorY, top: g.top, spots });
      if (guess !== null) {
        furniture.push(guess);
        for (const c of part) used.add(`${c.gx},${c.gz}`);
        continue;
      }
      if (part.length * cell * cell >= SCAN_CG.stepKeepAreaM2) continue;
      if (isStair(part, stepTops, g.top)) continue;
      for (const c of part) flat.add(`${c.gx},${c.gz}`);
    }
    if (used.size > 0) asFurniture.set(key, used);
    if (flat.size > 0) asFloor.set(key, flat);
  }

  const rooms = [];
  const addFloor = (cells, y, bottom, texture) => {
    for (const r of mergeRectangles(cells)) {
      rooms.push({
        x: [toX(r.x0), toX(r.x1 + 1)],
        z: [toZ(r.z0), toZ(r.z1 + 1)],
        y,
        thick: Math.max(y - bottom, SCAN_CG.minSlab),
        floor: texture,
        under: SCAN_CG.underTexture,
      });
    }
  };
  for (const [key, { cells, bottom, top }] of floorGroups) {
    const li = levelIndexOf(top);
    const isStep = li < 0;
    const texture = isStep ? SCAN_CG.stepTexture : SCAN_CG.floorTextures[Math.min(li, SCAN_CG.floorTextures.length - 1)];
    // 同じ階はひとつの高さにそろえる。スキャンの床は波打っていて、そのままだと床が段々になる
    const y = isStep ? top : levels[li];
    const used = asFurniture.get(key);
    const flat = asFloor.get(key);
    const keep = cells.filter(
      (c) => !used?.has(`${c.gx},${c.gz}`) && !flat?.has(`${c.gx},${c.gz}`),
    );
    addFloor(keep, y, bottom, texture);
    // 波打ちとみなした段は、一番近い階の床の高さで平らに敷き直す
    if (flat === undefined) continue;
    const level = nearestLevel(levels, top);
    const li2 = levelIndexOf(level);
    addFloor(
      cells.filter((c) => flat.has(`${c.gx},${c.gz}`)),
      level,
      bottom,
      SCAN_CG.floorTextures[Math.min(Math.max(li2, 0), SCAN_CG.floorTextures.length - 1)],
    );
  }

  /*
   * ---- 天井 ----
   * 当たり判定マップが持つ「頭上の水平面」の高さを集め、その中央値に板を張る。
   * 中二階やロフトの床裏は、その床板の下面がそのまま天井になるので数えない。
   * スキャンは天井を撮り逃していることが多いので、見つからなければ一番上の階の
   * 少し上に平らな天井を張る（黒い空が見えるより、部屋らしい）。
   */
  const topLevel = levels[levels.length - 1] ?? layout.baseY;
  const measured = [];
  if (nav !== null && typeof nav.ceilingAt === 'function') {
    for (const { cells, top } of floorGroups.values()) {
      if (levelIndexOf(top) < 0) continue;
      for (const c of cells) {
        const h = nav.ceilingAt(toX(c.gx) + cell / 2, toZ(c.gz) + cell / 2, top);
        if (h !== null && h - top >= SCAN_CG.minCeilingHeight && !levels.some((o) => Math.abs(o - h) < 0.4)) {
          measured.push(h);
        }
      }
    }
  }
  measured.sort((a, b) => a - b);
  const ceiling = measured.length >= SCAN_CG.ceilingMinSamples
    ? measured[Math.floor(measured.length / 2)]
    : topLevel + SCAN_CG.defaultCeilingAbove;
  // 一番下の階の床の範囲すべてに天井を張る（上の階の床板が間にあれば、それが下から見える）
  const baseLevel = levels[0] ?? layout.baseY;
  const baseCells = [];
  for (const { cells, top } of floorGroups.values()) if (levelIndexOf(top) === 0) baseCells.push(...cells);
  for (const r of mergeRectangles(baseCells)) {
    rooms.push({
      x: [toX(r.x0), toX(r.x1 + 1)],
      z: [toZ(r.z0), toZ(r.z1 + 1)],
      y: baseLevel,
      h: ceiling - baseLevel,
      ceil: SCAN_CG.ceilingTexture,
      ceilThick: SCAN_CG.ceilingThick,
    });
  }

  /*
   * ---- 壁 ----
   * 床の外周に壁を立てる（間取り図と同じ考え方）。
   *
   * スキャンが「壁らしい」と判定した縦の面をそのまま壁にすると、
   * 洗面台・カーテン・スキャンのノイズまで壁になり、部屋の中に柱が林立してしまう。
   * 床が途切れる所＝部屋の境目なので、そこに薄い壁を立てる方が実際の間取りに近い。
   * 出入口は床がつながっているため、自然に開口部として残る。
   */
  const walls = [];
  for (const [li, level] of levels.entries()) {
    for (const edge of floorBoundaries(levelFill.get(li) ?? new Set())) {
      /*
       * 部屋を囲む壁は一番下の階だけ。中二階・ロフトの縁は手すりにする。
       * 上の階の縁にも天井まで壁を立てると、そこに立ったとき一面が壁になって下が見えない。
       * 外壁は一番下の階の壁が天井まで伸びているので、手すりだけでも外に穴は開かない。
       */
      const solid = li === 0;
      const h = solid ? Math.max(ceiling - level, SCAN_CG.minWallHeight) : SCAN_CG.railingHeight;
      walls.push({
        from: edge.axis === 'x' ? [toX(edge.line), toZ(edge.from)] : [toX(edge.from), toZ(edge.line)],
        to: edge.axis === 'x' ? [toX(edge.line), toZ(edge.to)] : [toX(edge.to), toZ(edge.line)],
        y: level,
        h,
        t: solid ? SCAN_CG.wallThickness : SCAN_CG.railingThickness,
        mat: solid ? SCAN_CG.wallTexture : SCAN_CG.railingTexture,
        skirting: false,
      });
    }
  }

  /*
   * ---- 内側の仕切り（洗面所・浴室・クローゼットの壁）----
   * 外周だけでは間取りが伝わらないので、部屋の中の壁も立てる。
   * スキャンが縦の面と見たマスのうち、細長く続くものだけを壁とみなす。
   * 短い塊（洗面台・冷蔵庫・カーテン）まで拾うと、部屋の中に柱が林立してしまう。
   * 出入口にはスキャンの壁が無いので、開口部として自然に残る。
   */
  const baseFill = levelFill.get(0) ?? new Set();
  for (const group of connectedGroups(scanWalls)) {
    // かたまりの形を見る。壁は線のように細長く、洗面台や冷蔵庫は詰まった塊になる
    const gxs = group.map((c) => c.gx);
    const gzs = group.map((c) => c.gz);
    const w = (Math.max(...gxs) - Math.min(...gxs) + 1) * cell;
    const d = (Math.max(...gzs) - Math.min(...gzs) + 1) * cell;
    const filled = (group.length * cell * cell) / (w * d);
    if (Math.min(w, d) > SCAN_CG.innerWallMaxThickness && filled > SCAN_CG.innerWallMaxFill) continue;

    for (const r of mergeRectangles(group)) {
      const rw = (r.x1 - r.x0 + 1) * cell;
      const rd = (r.z1 - r.z0 + 1) * cell;
      if (Math.max(rw, rd) < SCAN_CG.innerWallMinLength) continue;
      if (Math.min(rw, rd) > SCAN_CG.innerWallMaxThickness) continue;
      if (!coversAll(r, baseFill)) continue;
      const alongX = rw >= rd;
      const cx = toX(r.x0) + rw / 2;
      const cz = toZ(r.z0) + rd / 2;
      walls.push({
        from: alongX ? [toX(r.x0), cz] : [cx, toZ(r.z0)],
        to: alongX ? [toX(r.x1 + 1), cz] : [cx, toZ(r.z1 + 1)],
        y: baseLevel,
        h: ceiling - baseLevel,
        t: Math.min(rw, rd),
        mat: SCAN_CG.wallTexture,
        skirting: false,
      });
    }
  }

  /*
   * ---- 家具（低い物のかたまりから拾う）----
   * 床にも段にもならなかった「腰より低い物」を、隣り合うマスごとにまとめる。
   * 大きさから何の家具か当てられたら本物の家具の形で、当てられなければ
   * スキャンの色を付けた箱で置く（無理に当てると、実際には無い家具を見せることになる）。
   */
  for (const cluster of clusterFurniture(layout.furniture, layout.furnitureCell)) {
    const floorY = nearestLevel(levels, Math.min(...cluster.map((c) => c.bottom)));
    const guess = guessFurniture(cluster, { cell: layout.furnitureCell, minX, minZ, floorY, spots });
    furniture.push(
      guess ?? { type: 'cluster', x: 0, z: 0, cell: layout.furnitureCell, minX, minZ, cells: cluster },
    );
  }
  return { id: 'scan', rooms, walls, furniture, lights: [], ambient: SCAN_CG.ambient, sun: SCAN_CG.sun, levels };
}

/**
 * 床のマスの集まりの外周を、壁にできる線分として取り出す。
 * 各マスの上下左右で、隣に床が無い所が外周。同じ直線上で続いているものは1枚の壁にまとめる。
 *
 * @param cells 「gx,gz」の集合
 * @returns [{ axis: 'x'|'z', line, from, to, cells }] axis は線の向き。
 *          'x' なら line が x のマス番号で from..to が z の範囲（z に沿って伸びる壁）
 */
function floorBoundaries(cells) {
  const outside = outsideCells(cells);
  const lines = { x: new Map(), z: new Map() };
  for (const key of cells) {
    const [gx, gz] = key.split(',').map(Number);
    // ox, oz は縁の外側のマス。壁にするか手すりにするかの判断に使う
    if (outside.has(`${gx - 1},${gz}`)) addBoundary(lines.x, gx, gz, { gx, gz, ox: gx - 1, oz: gz });
    if (outside.has(`${gx + 1},${gz}`)) addBoundary(lines.x, gx + 1, gz, { gx, gz, ox: gx + 1, oz: gz });
    if (outside.has(`${gx},${gz - 1}`)) addBoundary(lines.z, gz, gx, { gx, gz, ox: gx, oz: gz - 1 });
    if (outside.has(`${gx},${gz + 1}`)) addBoundary(lines.z, gz + 1, gx, { gx, gz, ox: gx, oz: gz + 1 });
  }

  const edges = [];
  for (const axis of ['x', 'z']) {
    for (const [line, items] of lines[axis]) {
      let run = null;
      for (const [v, owners] of [...items.entries()].sort((a, b) => a[0] - b[0])) {
        if (run !== null && v === run.to) {
          run.to = v + 1;
          run.cells.push(...owners);
          continue;
        }
        if (run !== null) edges.push(run);
        run = { axis, line, from: v, to: v + 1, cells: [...owners] };
      }
      if (run !== null) edges.push(run);
    }
  }
  return edges;
}

/**
 * 階ごとに「部屋の形」を決める。
 *
 * スキャンの床は「歩けた所」だけなので、縁がギザギザで、家具の下や撮り逃した所が抜けている。
 * そのまま床にすると床に黒い穴が空き、壁も 0.2m ごとに細切れになる。
 * そこで縁をならし、外周の内側をすべて床で埋め、その形を床と壁の両方で使う。
 *
 * @returns Map<階の番号, 「gx,gz」の集合>。足したマスは floorGroups にも入れる
 */
function fillLevels(floorGroups, levels, levelIndexOf, spotCells, scanWalls) {
  const fill = new Map();
  const scannedAt = new Map();
  const hostAt = new Map();

  for (let li = 0; li < levels.length; li += 1) {
    const groups = [...floorGroups.values()].filter((g) => levelIndexOf(g.top) === li);
    if (groups.length === 0) {
      fill.set(li, new Set());
      continue;
    }
    // scanned は「もう床として持っているマス」。あとで足すマスと区別するため、視点のぶんは入れない
    const scanned = new Set();
    for (const g of groups) for (const c of g.cells) scanned.add(`${c.gx},${c.gz}`);
    scannedAt.set(li, scanned);
    // 足したマスは、その階で一番広い床のかたまりに入れる（同じ高さ・同じ厚さの床になる）
    hostAt.set(li, groups.reduce((a, b) => (a.cells.length >= b.cells.length ? a : b)));
    const seed = new Set(scanned);
    for (const key of spotCells.get(li) ?? []) seed.add(key);
    fill.set(li, solidify(seed, scanWalls));
  }

  /*
   * 一番下の階の床には、上の階の真下も足す。
   * 中二階やロフトの下はスキャンでは歩けないので床が抜けるが、実際にはそこにも床がある。
   * 抜けたままだと部屋の真ん中が床の「縁」になり、そこに天井までの壁が立ってしまう。
   */
  const base = fill.get(0);
  if (base !== undefined) {
    for (const [li, cells] of fill) if (li > 0) for (const key of cells) base.add(key);
    fill.set(0, solidify(base, scanWalls));
  }

  for (const [li, cells] of fill) {
    const host = hostAt.get(li);
    const scanned = scannedAt.get(li);
    if (host === undefined) continue;
    for (const key of cells) {
      if (scanned.has(key)) continue;
      const [gx, gz] = key.split(',').map(Number);
      host.cells.push({ gx, gz, color: 0 });
    }
  }
  return fill;
}

/** マスの集まりの縁をならし、囲いの中の抜けを埋めて、部屋ひとつぶんの床の形にする */
function solidify(cells, blocked) {
  const closed = closeGaps(cells, SCAN_CG.wallSmoothCells);
  const solid = new Set(closed);
  // 外から塗りつぶして届かなかったマス＝囲いの中の抜け。ここも床にする。
  // スキャンが壁と見た所（blocked）は塗りつぶしを通さないので、洗面所の中なども「中」になる
  const outside = outsideCells(closed, blocked);
  const [x0, x1, z0, z1] = boundsOf(closed);
  for (let gz = z0; gz <= z1; gz += 1) {
    for (let gx = x0; gx <= x1; gx += 1) {
      const key = `${gx},${gz}`;
      if (!outside.has(key)) solid.add(key);
    }
  }
  for (const key of spanFill(closed)) solid.add(key);
  return straighten(solid);
}

/**
 * 部屋の輪郭をまっすぐにする。
 *
 * スキャンの床は 0.2m のマス単位で縁がギザギザなので、そのまま壁を立てると階段状になり、
 * 間取り図のような直線の部屋に見えない。
 * 行ごとの左右の端・列ごとの上下の端を、近い値どうしでひとまとめにして同じ位置へそろえる。
 * 大きく出っ張っている所（バルコニーなど）は別のまとまりになるので、つぶれない。
 */
function straighten(cells) {
  const rows = new Map();
  const cols = new Map();
  for (const key of cells) {
    const [gx, gz] = key.split(',').map(Number);
    span(rows, gz, gx);
    span(cols, gx, gz);
  }
  const tol = SCAN_CG.wallSnapCells;
  const rowLo = snapRuns(rows, 0, tol);
  const rowHi = snapRuns(rows, 1, tol);
  const colLo = snapRuns(cols, 0, tol);
  const colHi = snapRuns(cols, 1, tol);

  // 行でそろえた形と列でそろえた形の重なりを取る。片方だけの出っ張りに引きずられないようにする
  const byRow = new Set();
  for (const [gz, lo] of rowLo) for (let gx = lo; gx <= rowHi.get(gz); gx += 1) byRow.add(`${gx},${gz}`);
  const out = new Set();
  for (const [gx, lo] of colLo) {
    for (let gz = lo; gz <= colHi.get(gx); gz += 1) {
      const key = `${gx},${gz}`;
      if (byRow.has(key)) out.add(key);
    }
  }
  return out;
}

/**
 * 端の位置の並びを、続いているまとまりごとに代表値（一番多い値）へそろえる。
 * 前の行と tol マスより大きく違ったら、そこで別のまとまりにする。
 */
function snapRuns(ranges, index, tol) {
  const keys = [...ranges.keys()].sort((a, b) => a - b);
  const out = new Map();
  let run = [];
  const flush = () => {
    if (run.length === 0) return;
    const counts = new Map();
    for (const k of run) {
      const v = ranges.get(k)[index];
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    let best = ranges.get(run[0])[index];
    let most = -1;
    for (const [v, n] of counts) if (n > most) { most = n; best = v; }
    for (const k of run) out.set(k, best);
    run = [];
  };
  for (const k of keys) {
    if (run.length > 0 && Math.abs(ranges.get(k)[index] - ranges.get(run[run.length - 1])[index]) > tol) flush();
    run.push(k);
  }
  flush();
  return out;
}

/**
 * 縦と横の両方で床にはさまれているマスを埋める。
 *
 * スキャンの床は、家具の陰や撮り逃しで部屋の途中がごっそり抜けることがある。
 * 抜けが部屋の縁までつながっていると「外」とみなされて埋まらず、床に黒い穴が残る。
 * 行でも列でも床にはさまれている＝部屋の中、と判断して埋める。
 * L字の部屋の外側の角は片方でしかはさまれないので、埋まらない。
 */
function spanFill(cells) {
  const rows = new Map();
  const cols = new Map();
  for (const key of cells) {
    const [gx, gz] = key.split(',').map(Number);
    span(rows, gz, gx);
    span(cols, gx, gz);
  }
  const filled = new Set();
  for (const [gz, [lo, hi]] of rows) {
    for (let gx = lo; gx <= hi; gx += 1) {
      const col = cols.get(gx);
      if (col !== undefined && gz >= col[0] && gz <= col[1]) filled.add(`${gx},${gz}`);
    }
  }
  return filled;
}

function span(map, key, v) {
  const range = map.get(key);
  if (range === undefined) map.set(key, [v, v]);
  else {
    range[0] = Math.min(range[0], v);
    range[1] = Math.max(range[1], v);
  }
}

/**
 * 視点の足元を、階ごとのマスに直す。
 * 視点まわり SCAN_CG.spotFloorCells マスを床にして、近くの床とつながるようにする。
 */
function spotCellsByLevel(spots, levels, layout) {
  const byLevel = new Map();
  const r = SCAN_CG.spotFloorCells;
  for (const { x, y, z } of spots) {
    let li = -1;
    let best = Infinity;
    for (const [i, h] of levels.entries()) {
      const d = Math.abs(h - y);
      if (d < best) {
        best = d;
        li = i;
      }
    }
    if (li < 0 || best > SCAN_CG.spotLevelTolerance) continue;
    const gx = Math.floor((x - layout.minX) / layout.cell);
    const gz = Math.floor((z - layout.minZ) / layout.cell);
    if (!byLevel.has(li)) byLevel.set(li, new Set());
    const cells = byLevel.get(li);
    for (let dz = -r; dz <= r; dz += 1) {
      for (let dx = -r; dx <= r; dx += 1) if (dx * dx + dz * dz <= r * r) cells.add(`${gx + dx},${gz + dz}`);
    }
  }
  return byLevel;
}

/**
 * その段が階段の一段かどうか。
 * 階段は段が連なるので、隣（斜めも含む）に蹴上げぶん高さの違う段がある。
 * スキャンの波打ちで出た段は、隣の段との差がもっと小さい。
 */
function isStair(part, stepTops, top) {
  for (const c of part) {
    for (let dz = -1; dz <= 1; dz += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const other = stepTops.get(`${c.gx + dx},${c.gz + dz}`);
        if (other === undefined) continue;
        const rise = Math.abs(other - top);
        if (rise >= SCAN_CG.stairRiseMin && rise <= SCAN_CG.stairRiseMax) return true;
      }
    }
  }
  return false;
}

/** つながっているマス（上下左右）をひとかたまりにする */
function connectedGroups(keys) {
  const remaining = new Set(keys);
  const groups = [];
  for (const start of keys) {
    if (!remaining.has(start)) continue;
    remaining.delete(start);
    const group = [];
    const stack = [start];
    while (stack.length > 0) {
      const key = stack.pop();
      const [gx, gz] = key.split(',').map(Number);
      group.push({ gx, gz });
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const k = `${gx + dx},${gz + dz}`;
        if (!remaining.has(k)) continue;
        remaining.delete(k);
        stack.push(k);
      }
    }
    groups.push(group);
  }
  return groups;
}

/** 長方形の全マスが cells に入っているか（部屋の中にある壁かどうかの判定） */
function coversAll(r, cells) {
  for (let gz = r.z0; gz <= r.z1; gz += 1) {
    for (let gx = r.x0; gx <= r.x1; gx += 1) if (!cells.has(`${gx},${gz}`)) return false;
  }
  return true;
}

/** マスの集まりを囲む範囲 [x0, x1, z0, z1] */
function boundsOf(cells) {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  for (const key of cells) {
    const [gx, gz] = key.split(',').map(Number);
    x0 = Math.min(x0, gx);
    x1 = Math.max(x1, gx);
    z0 = Math.min(z0, gz);
    z1 = Math.max(z1, gz);
  }
  return [x0, x1, z0, z1];
}

/**
 * マスの集まりの縁をならす（モルフォロジーのクロージング）。
 * radius マスぶん膨らませてから同じだけ縮めると、幅 2*radius までの凹みと隙間が埋まり、
 * もとからあったマスは残る。壁の枚数を減らし、まっすぐな壁にするために使う。
 */
function closeGaps(cells, radius) {
  let grown = cells;
  for (let r = 0; r < radius; r += 1) grown = neighbourhood(grown, (next, k) => next.add(k));
  for (let r = 0; r < radius; r += 1) {
    const next = new Set();
    for (const key of grown) if (surroundedBy(grown, key)) next.add(key);
    grown = next;
  }
  return grown;
}

function neighbourhood(cells, put) {
  const next = new Set(cells);
  for (const key of cells) {
    const [gx, gz] = key.split(',').map(Number);
    for (let dz = -1; dz <= 1; dz += 1) for (let dx = -1; dx <= 1; dx += 1) put(next, `${gx + dx},${gz + dz}`);
  }
  return next;
}

function surroundedBy(cells, key) {
  const [gx, gz] = key.split(',').map(Number);
  for (let dz = -1; dz <= 1; dz += 1) {
    for (let dx = -1; dx <= 1; dx += 1) if (!cells.has(`${gx + dx},${gz + dz}`)) return false;
  }
  return true;
}

/** 床のマスの外側（囲いの外からたどり着ける空きマス）。部屋の中の抜けは含まない */
function outsideCells(cells, blocked = new Set()) {
  if (cells.size === 0) return new Set();
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  for (const key of cells) {
    const [gx, gz] = key.split(',').map(Number);
    x0 = Math.min(x0, gx - 1);
    x1 = Math.max(x1, gx + 1);
    z0 = Math.min(z0, gz - 1);
    z1 = Math.max(z1, gz + 1);
  }
  const outside = new Set([`${x0},${z0}`]);
  const stack = [[x0, z0]];
  while (stack.length > 0) {
    const [gx, gz] = stack.pop();
    for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = gx + dx;
      const nz = gz + dz;
      if (nx < x0 || nx > x1 || nz < z0 || nz > z1) continue;
      const k = `${nx},${nz}`;
      if (outside.has(k) || cells.has(k) || blocked.has(k)) continue;
      outside.add(k);
      stack.push([nx, nz]);
    }
  }
  return outside;
}

function addBoundary(map, line, v, cell) {
  let items = map.get(line);
  if (items === undefined) {
    items = new Map();
    map.set(line, items);
  }
  const owners = items.get(v);
  if (owners === undefined) items.set(v, [cell]);
  else owners.push(cell);
}

/**
 * 床の高さの一覧から「階」を決める。
 * スキャンの床は波打っているので、近い高さはひとつにまとめ、マスの多い高さを代表にする。
 * まとめた結果、十分な広さ（minCells）があるものだけを階とみなす。
 */
function clusterLevels(levelCounts, minCells) {
  const sorted = [...levelCounts.entries()].sort((a, b) => a[0] - b[0]);
  const clusters = [];
  for (const [height, count] of sorted) {
    const last = clusters[clusters.length - 1];
    if (last && height - last.heights[last.heights.length - 1] <= SCAN_CG.levelMergeGap) {
      last.heights.push(height);
      last.counts.push(count);
    } else {
      clusters.push({ heights: [height], counts: [count] });
    }
  }
  return clusters
    .map(({ heights, counts }) => {
      const total = counts.reduce((a, b) => a + b, 0);
      // 一番マスの多い高さを、その階の高さにする
      const best = heights[counts.indexOf(Math.max(...counts))];
      return { height: best, total };
    })
    .filter(({ total }) => total >= minCells)
    .map(({ height }) => height)
    .sort((a, b) => a - b);
}

/**
 * 家具の下にも床を敷く。
 * 見取りの床は「歩ける所」だけなので、家具の下は床が抜けて真っ黒に見えてしまう。
 * 家具のマス（細かい）を床のマス（粗い）に直し、足元の高さに一番近い階の床として足す。
 */
function fillFloorUnderFurniture(floorGroups, layout) {
  const ratio = layout.cell / layout.furnitureCell;
  const existing = new Set();
  for (const { cells } of floorGroups.values()) for (const c of cells) existing.add(`${c.gx},${c.gz}`);
  const groups = [...floorGroups.values()];
  for (let i = 0; i < layout.furniture.length; i += S) {
    const gx = Math.floor(layout.furniture[i] / ratio);
    const gz = Math.floor(layout.furniture[i + 1] / ratio);
    const key = `${gx},${gz}`;
    if (existing.has(key)) continue;
    const bottom = layout.furniture[i + 2];
    const group = groups.reduce((best, g) => (Math.abs(g.top - bottom) < Math.abs(best.top - bottom) ? g : best));
    if (Math.abs(group.top - bottom) > SCAN_CG.clusterBaseTolerance) continue;
    group.cells.push({ gx, gz, color: layout.furniture[i + 4] });
    existing.add(key);
  }
}

/**
 * 床を壁際まで広げる。
 * 見取りの床は「体の幅をとって歩ける所」なので、壁沿いの1マスが抜けて黒い筋になる。
 * 床のマスの隣で、壁でも床でもないマスを同じ高さの床として足す（SCAN_CG.floorGrowCells マスぶん）。
 */
function extendFloorsToWalls(floorGroups, layout) {
  const walls = new Set();
  for (let i = 0; i < layout.walls.length; i += S) walls.add(`${layout.walls[i]},${layout.walls[i + 1]}`);
  const owner = new Map();
  for (const g of floorGroups.values()) for (const c of g.cells) owner.set(`${c.gx},${c.gz}`, g);

  for (let pass = 0; pass < SCAN_CG.floorGrowCells; pass += 1) {
    const added = [];
    for (const [key, g] of owner) {
      const [gx, gz] = key.split(',').map(Number);
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1]]) {
        const k = `${gx + dx},${gz + dz}`;
        if (owner.has(k) || walls.has(k)) continue;
        added.push([k, gx + dx, gz + dz, g]);
      }
    }
    for (const [k, gx, gz, g] of added) {
      if (owner.has(k)) continue;
      g.cells.push({ gx, gz, color: 0 });
      owner.set(k, g);
    }
  }
}

/** [gx, gz, bottom, top, 色] の並びを、key(bottom, top) ごとに {cells, bottom, top} にまとめる */
function groupBy(flat, keyOf) {
  const groups = new Map();
  for (let i = 0; i < flat.length; i += S) {
    const bottom = round(flat[i + 2], 2);
    const top = round(flat[i + 3], 2);
    const key = keyOf(bottom, top);
    if (!groups.has(key)) groups.set(key, { cells: [], bottom, top });
    groups.get(key).cells.push({ gx: flat[i], gz: flat[i + 1], color: flat[i + 4] });
  }
  return groups;
}

/**
 * マスの集まりを、できるだけ少ない長方形に分ける（貪欲法）。
 * 左上から見て、右へ伸ばせるだけ伸ばし、その幅のまま下へ伸ばせるだけ伸ばす。
 */
export function mergeRectangles(cells) {
  const key = (gx, gz) => `${gx},${gz}`;
  const free = new Set(cells.map((c) => key(c.gx, c.gz)));
  const sorted = [...cells].sort((a, b) => a.gz - b.gz || a.gx - b.gx);
  const rects = [];
  for (const c of sorted) {
    if (!free.has(key(c.gx, c.gz))) continue;
    let x1 = c.gx;
    while (free.has(key(x1 + 1, c.gz))) x1 += 1;
    let z1 = c.gz;
    while (rowFree(free, key, c.gx, x1, z1 + 1)) z1 += 1;
    for (let gz = c.gz; gz <= z1; gz += 1) for (let gx = c.gx; gx <= x1; gx += 1) free.delete(key(gx, gz));
    rects.push({ x0: c.gx, x1, z0: c.gz, z1 });
  }
  return rects;
}

function rowFree(free, key, x0, x1, gz) {
  for (let gx = x0; gx <= x1; gx += 1) if (!free.has(key(gx, gz))) return false;
  return true;
}

/** 家具のマス（上下左右がつながっていて、足元の高さが近いもの）をひとかたまりにする */
export function clusterFurniture(flat, cell) {
  const items = [];
  for (let i = 0; i < flat.length; i += S) {
    items.push({ gx: flat[i], gz: flat[i + 1], bottom: flat[i + 2], top: flat[i + 3], color: flat[i + 4] });
  }
  const index = new Map(items.map((it, i) => [`${it.gx},${it.gz}`, i]));
  const seen = new Set();
  const clusters = [];
  for (let i = 0; i < items.length; i += 1) {
    if (seen.has(i)) continue;
    const cluster = [];
    const stack = [i];
    seen.add(i);
    while (stack.length > 0) {
      const j = stack.pop();
      const it = items[j];
      cluster.push(it);
      for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
        const k = index.get(`${it.gx + dx},${it.gz + dz}`);
        if (k === undefined || seen.has(k)) continue;
        if (Math.abs(items[k].bottom - it.bottom) > SCAN_CG.clusterBaseTolerance) continue;
        seen.add(k);
        stack.push(k);
      }
    }
    // 1〜2マスのゴミは家具として扱わない
    if (cluster.length * cell * cell >= SCAN_CG.furnitureMinAreaM2) clusters.push(cluster);
  }
  return clusters;
}

/** 一番近い階の高さ */
const nearestLevel = (levels, y) => levels.reduce((best, h) => (Math.abs(h - y) < Math.abs(best - y) ? h : best), levels[0] ?? y);

const round = (v, digits) => Math.round(v * 10 ** digits) / 10 ** digits;
